// Carla's item 4 (2026-10-12): the Team dashboard's task lists are searched and paged ON THE SERVER,
// 15 at a time (GET /api/tasks?team=true&status=&member=&q=&page=&pageSize=; Aidey Backend
// lib/teamTaskPage.js), so a column's count and the page numbers are the server's, never one page's.
// The scoping is the server's too: the same rows the full list would have had.
import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';

export const PAGE_SIZE = 15;

export function teamTasksUrl({ status = 'all', member = '', q = '', page = 1 }) {
  const p = new URLSearchParams({ team: 'true', status });
  if (member) p.set('member', member);
  if (q) p.set('q', q);
  p.set('page', String(page));
  p.set('pageSize', String(PAGE_SIZE));
  return `/api/tasks?${p.toString()}`;
}

const EMPTY = { tasks: [], total: 0, pages: 1, loading: false, error: '' };

// One paged list. A change of status, member or search starts again at page 1 (the page is kept
// against the filters it was chosen for, so the change is one request, not two). reloadKey asks for
// the same page again after a change elsewhere (a save, a move, a delete).
export function useTaskPage({ status = 'all', member = '', q = '', enabled = true, reloadKey = 0 }) {
  const sig = `${status}|${member}|${q}`;
  const [at, setAt] = useState({ sig, page: 1 });
  const page = at.sig === sig ? at.page : 1;
  const [state, setState] = useState({ ...EMPTY, loading: enabled });
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (!enabled) { setState(EMPTY); return undefined; }
    let live = true;
    setState((s) => ({ ...s, loading: true, error: '' }));
    (async () => {
      try {
        const res = await api(teamTasksUrl({ status, member, q, page }));
        const data = await res.json();
        if (!live) return;
        // A 200 without a list is a failure, never an empty column.
        if (!Array.isArray(data?.tasks)) throw new Error('The task list did not come back. Refresh to try again.');
        setState({ tasks: data.tasks, total: Number(data.total) || 0, pages: Math.max(1, Number(data.pages) || 1), loading: false, error: '' });
        // The server holds a page past the end to the last one (after a delete, say): follow it.
        if (Number(data.page) && Number(data.page) !== page) setAt({ sig, page: Number(data.page) });
      } catch (err) {
        if (live) setState((s) => ({ ...s, loading: false, error: messageFromError(err, 'These tasks could not be loaded.') }));
      }
    })();
    return () => { live = false; };
  }, [status, member, q, page, enabled, reloadKey, retry]);   // eslint-disable-line react-hooks/exhaustive-deps

  return {
    ...state,
    page,
    setPage: (p) => setAt({ sig, page: p }),
    retry: () => setRetry((n) => n + 1),
  };
}
